import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuItem,
  Box,
  Text,
  Icon,
  Input,
  Button,
  Checkbox,
  Command,
  CommandDialog,
  CommandDialogPopup,
  CommandEmpty,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  CommandItem,
  CommandList,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  Label,
  ScrollArea,
  Separator,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  InlineRow,
  InlineRowContent,
  InlineHeaderRow,
  InlineRowActions,
  useIsMobile,
} from "@salestrak/ui";
import {
  dataToField,
  fieldDto,
  getDefaultValueByType,
} from "@salestrak/custom-fields";
import { useAvailableCustomFields } from "#app/hooks/issue-view/use-available-custom-fields";
import { useIssueViewCapabilities } from "./issue-view-capabilities";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { useScrollToElement } from "#app/lib/hooks/use-scroll-to-element";
import type { CustomFieldDef } from "./issue-view-types";
import type { IssueRow } from "#app/lib/data";
import {
  getCustomFieldDisplayValue,
  isCustomFieldValueEmpty,
} from "../custom-fields/custom-field-renderers";
import {
  CustomFieldValueCell,
  CUSTOM_FIELD_VALUE_CELL_HEIGHT,
  type CustomFieldValueCellApi,
} from "../custom-fields/custom-field-value-cell";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import type { Omittable } from "@salestrak/ui";

const DESKTOP_FIELD_COLUMN_WIDTH = 220;
const MOBILE_FIELD_COLUMN_MIN_WIDTH = 180;
const MOBILE_FIELD_COLUMN_MAX_WIDTH = 180;
const MOBILE_VALUE_COLUMN_MIN_WIDTH = 160;
const MOBILE_VALUE_COLUMN_MAX_WIDTH = 260;
const MOBILE_CHAR_WIDTH_PX = 9;

type CustomFieldRowProps = Omittable<{
  cfId: string;
  field: CustomFieldDef | undefined;
  value: unknown;
  isActive?: boolean;
  fieldColumnWidth?: number;
  valueColumnWidth?: number;
  required?: boolean;
  error?: string;
  showRemove?: boolean;
  id?: string;
  highlighted?: boolean;
  beforeHighlighted?: boolean;
  onRowHighlightChange?: (cfId: string, open: boolean) => void;
  onInvalidStateChange?: (cfId: string, invalid: boolean) => void;
  onUpdate: (cfId: string, value: unknown) => void;
  // Photos-field delta writes (view mode only). Present → the renderer commits
  // each landed upload / removed tile as a single-photo add/remove instead of
  // a full-array overwrite, so concurrent uploads can't clobber each other.
  onAddPhoto?: (cfId: string, photoId: string) => void | Promise<void>;
  onRemovePhoto?: (cfId: string, photoId: string) => void | Promise<void>;
  onRemove: (cfId: string) => void;
  onActivate?: (cfId: string) => void;
  onDeactivate?: () => void;
  // When true, the row renders the display value with no hover, no
  // cursor pointer, no editor activation, and no edit/clear/remove
  // actions. Used for view / comment access tiers.
  readOnly?: boolean;
  /** Issue name — prefixes photo download filenames in the lightbox. */
  downloadPrefix?: string;
}>;

/**
 * One field row: the label column (type icon, description tooltip, remove
 * menu) beside the shared {@link CustomFieldValueCell}, which owns all value
 * display/editing behavior. The row adapts the cell's value-only callbacks to
 * the container's cfId-keyed ones.
 */
function CustomFieldRow({
  cfId,
  field,
  value,
  isActive = false,
  fieldColumnWidth,
  valueColumnWidth,
  required,
  error,
  showRemove = true,
  id,
  highlighted = false,
  beforeHighlighted = false,
  onRowHighlightChange,
  onInvalidStateChange,
  onUpdate,
  onAddPhoto,
  onRemovePhoto,
  onRemove,
  onActivate,
  onDeactivate,
  readOnly = false,
  downloadPrefix,
}: CustomFieldRowProps) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  // Synchronous mirror of the value cell's overlay/touch-reveal state: the
  // row highlight must light in the same commit the overlay opens, and the
  // container round-trip (onRowHighlightChange → highlighted) is a render
  // behind on the way up.
  const [cellOverlayOpen, setCellOverlayOpen] = useState(false);
  // The cell owns validation; the row mirrors the message for its label.
  const [cellValidationError, setCellValidationError] = useState<
    string | null
  >(null);
  const cellApiRef = useRef<CustomFieldValueCellApi | null>(null);

  const effectiveError = cellValidationError ?? error;
  const forceHighlight = highlighted || menuOpen || cellOverlayOpen;
  const isPhotosField = field?.type === "photos";
  const fieldTypeMeta = useMemo(
    () => (field ? getFieldTypeMeta(field.type) : null),
    [field],
  );

  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      setMenuOpen(open);
      onRowHighlightChange?.(cfId, open);
    },
    [cfId, onRowHighlightChange],
  );

  // The container moved the highlight to another row — close this row's menu
  // (the value cell dismisses its own overlays the same way).
  useEffect(() => {
    if (!highlighted) setMenuOpen(false);
  }, [highlighted]);

  const handleOverlayChange = useCallback(
    (open: boolean) => {
      setCellOverlayOpen(open);
      onRowHighlightChange?.(cfId, open);
    },
    [cfId, onRowHighlightChange],
  );

  const handleCellInvalidStateChange = useCallback(
    (invalid: boolean) => onInvalidStateChange?.(cfId, invalid),
    [cfId, onInvalidStateChange],
  );

  const handleCellUpdate = useCallback(
    (next: unknown) => onUpdate(cfId, next),
    [cfId, onUpdate],
  );

  const handleCellActivate = useMemo(
    () => (onActivate ? () => onActivate(cfId) : undefined),
    [cfId, onActivate],
  );
  const handleCellAddPhoto = useMemo(
    () =>
      onAddPhoto ? (photoId: string) => onAddPhoto(cfId, photoId) : undefined,
    [cfId, onAddPhoto],
  );
  const handleCellRemovePhoto = useMemo(
    () =>
      onRemovePhoto
        ? (photoId: string) => onRemovePhoto(cfId, photoId)
        : undefined,
    [cfId, onRemovePhoto],
  );

  return (
    <InlineRow
      id={id}
      height={isPhotosField ? undefined : CUSTOM_FIELD_VALUE_CELL_HEIGHT}
      minHeight={isPhotosField ? CUSTOM_FIELD_VALUE_CELL_HEIGHT : undefined}
      highlighted={forceHighlight}
      beforeHighlighted={beforeHighlighted}
    >
      {/* Label */}
      <InlineRowContent
        direction="row"
        align="center"
        gap="sm"
        padding="sm"
        width={fieldColumnWidth ?? DESKTOP_FIELD_COLUMN_WIDTH}
        shrink={false}
        onClick={
          !readOnly && isMobile && showRemove
            ? () => handleMenuOpenChange(true)
            : undefined
        }
      >
        <Box fill="flex" direction="row" align="center" gap="sm">
          {(() => {
            const labelBody = (
              <Box fill="flex" direction="row" align="center" gap="sm">
                {fieldTypeMeta ? (
                  <Icon
                    name={fieldTypeMeta.icon}
                    size="md"
                    color={effectiveError ? "destructive" : "light-grey"}
                  />
                ) : null}
                <Text
                  size="sm"
                  weight="normal"
                  truncate={1}
                  variant={effectiveError ? "destructive" : "muted"}
                >
                  {field?.name ?? cfId}
                  {required && <Text variant="destructive"> *</Text>}
                </Text>
              </Box>
            );
            // A field description surfaces as a hover tooltip on its label.
            return field?.description ? (
              // The tooltip trigger renders its own element, so the label body
              // gets a Box host rather than becoming a button nobody can act on.
              <Tooltip>
                <TooltipTrigger render={<Box direction="row" align="center" fill="flex" />}>
                  {labelBody}
                </TooltipTrigger>
                <TooltipPopup side="top" align="start">
                  <Box gap="2xs" maxWidth={280}>
                    <Text size="xs" weight="semibold">
                      {field.name}
                    </Text>
                    <Text size="xs" variant="muted">
                      {field.description}
                    </Text>
                  </Box>
                </TooltipPopup>
              </Tooltip>
            ) : (
              labelBody
            );
          })()}
          {effectiveError ? (
            <Tooltip>
              <TooltipTrigger render={<Box direction="row" align="center" />}>
                <Icon name="alert-circle" size="md" color="destructive" />
              </TooltipTrigger>
              <TooltipPopup side="right">
                <Text size="xs">{effectiveError}</Text>
              </TooltipPopup>
            </Tooltip>
          ) : null}
        </Box>
        {showRemove && (
          <InlineRowActions
            width={28}
            height={28}
            visibility={forceHighlight ? "always" : "hover"}
          >
            <Menu open={menuOpen} onOpenChange={handleMenuOpenChange}>
              <MenuTrigger
                render={
                  <Button
                    variant="outline"
                    size="icon-xs"
                    aria-label={`Open ${field?.name ?? "custom field"} menu`}
                  >
                    <Icon name="more-horizontal" size="md" />
                  </Button>
                }
              />
              <MenuPopup align="start">
                {isActive ? (
                  <MenuItem onClick={() => cellApiRef.current?.reset()}>
                    <Icon name="rotate-ccw" size="md" />
                    <Text>Reset value</Text>
                  </MenuItem>
                ) : null}
                <MenuItem variant="destructive" onClick={() => onRemove(cfId)}>
                  <Icon name="x" size="md" color="destructive" />
                  <Text>Remove field</Text>
                </MenuItem>
              </MenuPopup>
            </Menu>
          </InlineRowActions>
        )}
      </InlineRowContent>

      {/* Value */}
      <InlineRowContent
        fill={valueColumnWidth ? undefined : "flex"}
        width={valueColumnWidth}
        shrink={!valueColumnWidth}
        direction="row"
        align="stretch"
      >
        <CustomFieldValueCell
          field={field}
          value={value}
          isActive={isActive}
          highlighted={highlighted}
          onUpdate={handleCellUpdate}
          onOverlayChange={handleOverlayChange}
          onInvalidStateChange={handleCellInvalidStateChange}
          onValidationErrorChange={setCellValidationError}
          onAddPhoto={handleCellAddPhoto}
          onRemovePhoto={handleCellRemovePhoto}
          onActivate={handleCellActivate}
          onDeactivate={onDeactivate}
          suppressActions={menuOpen}
          readOnly={readOnly}
          downloadPrefix={downloadPrefix}
          apiRef={cellApiRef}
        />
      </InlineRowContent>
    </InlineRow>
  );
}

type IssueViewCustomFieldsProps = Omittable<{
  issue?: IssueRow;
  values?: Record<string, unknown>;
  customFields?: CustomFieldDef[];
  // Custom field ids attached to the issue type, in server sortOrder. Drives
  // row order in both create and view modes. Fields with values on the issue
  // that aren't in this list (legacy values from fields detached from the
  // type) are appended after the ordered list in view mode.
  orderedTypeFieldIds?: string[];
  onUpdateValue?: (data: { customFieldId: string; value: unknown }) => void;
  // Photos-field delta writes (view mode only — absent in create mode). Routes
  // each landed upload / removed tile to an atomic single-photo add/remove.
  onAddPhoto?: (customFieldId: string, photoId: string) => void | Promise<void>;
  onRemovePhoto?: (customFieldId: string, photoId: string) => void | Promise<void>;
  requiredFieldIds?: string[];
  onCloseIssue?: () => void;
  /** Scroll to + briefly highlight this field row on mount (deep-link). */
  focusCustomFieldId?: string | null;
  /** Embedded (widget) mode: the widget card owns the title header, so the
   *  internal header row is not rendered… */
  hideHeader?: boolean;
  /** …and the search value is controlled by the card's header actions. When
   *  set (even ""), it replaces the internal search state. */
  search?: string;
}>;

export function IssueViewCustomFields({
  issue,
  values = {},
  customFields = [],
  orderedTypeFieldIds = [],
  onUpdateValue,
  onAddPhoto,
  onRemovePhoto,
  requiredFieldIds = [],
  focusCustomFieldId,
  hideHeader,
  search,
}: IssueViewCustomFieldsProps) {
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  // Field management (create/manage dialogs) is gated; adding existing
  // workspace fields to an issue is plain issue editing and stays open.
  const canManageCustomFields = usePermission("custom_fields.manage");
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  const { canEdit } = useIssueViewCapabilities();
  const readOnly = !isCreateMode && !canEdit;
  const submitted = createCtx?.submitted ?? false;
  const requiredSet = useMemo(
    () => new Set(requiredFieldIds),
    [requiredFieldIds],
  );
  const fieldErrors = useMemo(() => {
    if (!submitted) return {};
    const errors: Record<string, string> = {};
    for (const cfId of requiredFieldIds) {
      const val = values[cfId];
      if (val === undefined || val === null || val === "") {
        errors[cfId] = "This field is required";
      } else if (Array.isArray(val) && val.length === 0) {
        errors[cfId] = "This field is required";
      }
    }
    return errors;
  }, [submitted, requiredFieldIds, values]);

  const [searchVisible, setSearchVisible] = useState(false);
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const searchValue = search ?? internalSearchValue;
  const [showEmptyFields, setShowEmptyFields] = useState(false);
  const [showOnlyRequired, setShowOnlyRequired] = useState(isCreateMode);
  const isMobile = useIsMobile();
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [openFieldOverlayId, setOpenFieldOverlayId] = useState<string | null>(
    null,
  );
  // Transient highlight for a deep-linked field (inbox mention notification).
  // Purely visual — unlike activeFieldId it does NOT put the row into edit mode.
  const [focusHighlightId, setFocusHighlightId] = useState<string | null>(null);
  const [canAutoFocus, setCanAutoFocus] = useState(false);
  // The Drawer reports settle directly, through this one callback.
  const handleSheetSettle = useCallback(
    (open: boolean) => setCanAutoFocus(open),
    [],
  );
  // Base UI's autocomplete has no `shouldFilter`/`keywords` pair, so the
  // add-field search is an explicit local query + substring match.
  const [addFieldQuery, setAddFieldQuery] = useState("");

  // Photos is a workspace-scoped feature flag: when off, photos-type fields are
  // dropped from the displayed list (defs stay intact so existing values don't
  // surface as orphan rows).
  const { hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);

  const customFieldsById = useMemo(() => {
    const map: Record<string, CustomFieldDef> = {};
    for (const f of customFields) {
      map[f.customFieldId] = f;
    }
    return map;
  }, [customFields]);

  // Create mode: render every field attached to the type (in sortOrder),
  // value-or-not. Falls back to the legacy createCtx-derived list if the
  // caller didn't pass orderedTypeFieldIds (defensive — IssueViewSummary
  // always passes it).
  const createModeFieldIds = useMemo(() => {
    if (!isCreateMode) return [];
    if (orderedTypeFieldIds.length > 0) return orderedTypeFieldIds;
    return createCtx.issueTypeFields
      .filter((f) => f.field !== null)
      .map((f) => f.customFieldId);
  }, [isCreateMode, createCtx, orderedTypeFieldIds]);

  // Transform raw values through dataToField
  const formValues = useMemo(() => {
    if (!values) return {} as Record<string, unknown>;
    return Object.keys(values).reduce<Record<string, unknown>>((acc, cfId) => {
      if (cfId === ".") return acc;
      const field = customFieldsById[cfId];
      if (!field) return acc;
      acc[cfId] = dataToField(field.type, values[cfId]).fieldValue;
      return acc;
    }, {});
  }, [values, customFieldsById]);

  // View mode: keep the same SET of rendered fields (those with a value
  // record on the issue) but order them by the issue type's sortOrder.
  // Fields whose customFieldId isn't in the type's ordered list (e.g. the
  // field was detached from the type after a value was stored) trail at
  // the bottom in their existing arbitrary order.
  const fieldIds = useMemo(() => {
    if (isCreateMode) return createModeFieldIds;
    const valueKeys = Object.keys(formValues);
    if (orderedTypeFieldIds.length === 0) return valueKeys;
    const valueKeySet = new Set(valueKeys);
    const ordered = orderedTypeFieldIds.filter((id) => valueKeySet.has(id));
    const orderedSet = new Set(ordered);
    const legacy = valueKeys.filter((id) => !orderedSet.has(id));
    return [...ordered, ...legacy];
  }, [isCreateMode, createModeFieldIds, formValues, orderedTypeFieldIds]);

  // Phone/address can carry metadata with no user-entered value — isCustomFieldValueEmpty treats those as empty.
  const fieldsWithValues = useMemo(
    () =>
      fieldIds.filter((id) => {
        const field = customFieldsById[id];
        return field ? !isCustomFieldValueEmpty(field, formValues[id]) : false;
      }),
    [customFieldsById, fieldIds, formValues],
  );

  const numberOfEmptyFields = fieldIds.length - fieldsWithValues.length;

  // Fields to display based on show/hide empty toggle (view) or required filter (create)
  const displayedFieldIds = useMemo(() => {
    let base: string[];
    if (searchValue) {
      base = fieldIds;
    } else if (isCreateMode) {
      // Only collapse to required-only when there actually are required
      // fields — otherwise the filter hides every row (and the "Show all
      // fields" toggle doesn't render), leaving an empty header table.
      base = showOnlyRequired && requiredSet.size > 0
        ? fieldIds.filter((id) => requiredSet.has(id))
        : fieldIds;
    } else {
      base = showEmptyFields ? fieldsWithValues : fieldIds;
    }
    if (searchValue) {
      const needle = searchValue.toLowerCase();
      base = base.filter((id) => {
        const field = customFieldsById[id];
        return (
          field &&
          (field.name.toLowerCase().includes(needle) ||
            field.type.toLowerCase().includes(needle))
        );
      });
    }
    // Hide photos-type fields when the feature flag is off. Orphan ids (no def)
    // are left untouched — `undefined?.type` is never "photos".
    return photosEnabled
      ? base
      : base.filter((id) => customFieldsById[id]?.type !== "photos");
  }, [
    fieldIds,
    fieldsWithValues,
    showEmptyFields,
    showOnlyRequired,
    isCreateMode,
    requiredSet,
    searchValue,
    customFieldsById,
    photosEnabled,
  ]);

  const hasFields = fieldIds.length > 0;

  useEffect(() => {
    // A row that leaves the list (field detached/deleted, e.g. by another
    // session) unmounts without reporting invalid=false, so drop any invalid
    // marker it left behind — a stale ref would refuse activation of every
    // other row for the rest of the mount.
    if (
      invalidFieldIdRef.current &&
      !displayedFieldIds.includes(invalidFieldIdRef.current)
    ) {
      invalidFieldIdRef.current = null;
    }
    if (!activeFieldId) return;
    if (!displayedFieldIds.includes(activeFieldId)) {
      setActiveFieldId(null);
    }
  }, [activeFieldId, displayedFieldIds]);

  // Deep-link focus: when opened from a paragraph-mention inbox item, scroll
  // to the field and briefly highlight it. Runs once per focus id, and only
  // once the issue's field set has loaded (so the row exists to scroll to).
  const { scrollTo } = useScrollToElement();
  const focusedFieldRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusCustomFieldId) {
      // A cleared focus re-arms the guard so the SAME field can be focused
      // again later (media-tab quick links clear between jumps).
      focusedFieldRef.current = null;
      return;
    }
    if (focusedFieldRef.current === focusCustomFieldId) return;
    if (!fieldIds.includes(focusCustomFieldId)) return; // wait for values to load
    focusedFieldRef.current = focusCustomFieldId;
    // Reveal hidden empty fields so a focused-but-empty target still renders.
    if (!fieldsWithValues.includes(focusCustomFieldId)) setShowEmptyFields(true);
    setFocusHighlightId(focusCustomFieldId);
    scrollTo(`cf-${focusCustomFieldId}`);
    const timer = setTimeout(() => setFocusHighlightId(null), 3000);
    return () => clearTimeout(timer);
  }, [focusCustomFieldId, fieldIds, fieldsWithValues, scrollTo]);

  const handleRemoveField = useCallback(
    (customFieldId: string) => {
      if (invalidFieldIdRef.current === customFieldId) {
        invalidFieldIdRef.current = null;
      }
      setActiveFieldId((current) =>
        current === customFieldId ? null : current,
      );
      onUpdateValue?.({ customFieldId, value: undefined });
    },
    [onUpdateValue],
  );

  const handleFieldUpdate = useCallback(
    (customFieldId: string, value: unknown) => {
      const field = customFieldsById[customFieldId];
      if (!field) return;
      // fieldDto coerces rather than throws; the commit layer surfaces errors.
      onUpdateValue?.({
        customFieldId,
        value: value === undefined ? undefined : fieldDto(field.type, value),
      });
    },
    [customFieldsById, onUpdateValue],
  );

  const handleCloseSearch = useCallback(() => {
    setInternalSearchValue("");
    setSearchVisible(false);
  }, []);

  // Tracks which row (if any) currently holds an invalid value. A ref because
  // the synchronous flow on a click-to-another-cell is: row A's input blurs →
  // commitFromRenderer marks row A invalid → row B's click handler runs (this
  // tick, no render yet) → handleActivateField fires. State wouldn't be visible
  // here yet; the ref is.
  const invalidFieldIdRef = useRef<string | null>(null);
  const handleInvalidStateChange = useCallback(
    (customFieldId: string, invalid: boolean) => {
      if (invalid) {
        invalidFieldIdRef.current = customFieldId;
      } else if (invalidFieldIdRef.current === customFieldId) {
        invalidFieldIdRef.current = null;
      }
    },
    [],
  );

  const handleActivateField = useCallback(
    (customFieldId: string) => {
      // Refuse to switch active rows while another row is stuck on an invalid
      // value — otherwise that row's edit cell would tear down despite its
      // local invalidRef gate, because activeFieldId is parent-owned.
      if (
        invalidFieldIdRef.current &&
        invalidFieldIdRef.current !== customFieldId
      ) {
        return;
      }
      setActiveFieldId(customFieldId);
      setOpenFieldOverlayId((current) =>
        current && current !== customFieldId ? null : current,
      );
    },
    [],
  );

  const handleDeactivateField = useCallback(() => {
    if (invalidFieldIdRef.current) return;
    setActiveFieldId(null);
  }, []);

  const handleNavigateToCreateField = useCallback(() => {
    setAddFieldDialogOpen(false);
    openCustomFieldsDialog();
  }, [openCustomFieldsDialog]);

  const handleOpenAddFieldDialog = useCallback(() => {
    setSelectedFieldIds([]);
    setAddFieldDialogOpen(true);
  }, []);

  const handleRowHighlightChange = useCallback(
    (customFieldId: string, open: boolean) => {
      setOpenFieldOverlayId((current) => {
        if (open) return customFieldId;
        return current === customFieldId ? null : current;
      });
    },
    [],
  );

  const highlightedFieldId = openFieldOverlayId ?? activeFieldId ?? focusHighlightId;
  const headerDividerSuppressed = highlightedFieldId === displayedFieldIds[0];

  const mobileFieldColumnWidth = useMemo(() => {
    if (!isMobile) return undefined;
    return displayedFieldIds.reduce((maxWidth, cfId) => {
      const label = customFieldsById[cfId]?.name ?? cfId;
      const estimatedWidth = Math.min(
        label.length * MOBILE_CHAR_WIDTH_PX + 88,
        MOBILE_FIELD_COLUMN_MAX_WIDTH,
      );
      return Math.max(maxWidth, estimatedWidth);
    }, MOBILE_FIELD_COLUMN_MIN_WIDTH);
  }, [customFieldsById, displayedFieldIds, isMobile]);

  const mobileValueColumnWidth = useMemo(() => {
    if (!isMobile) return undefined;
    return displayedFieldIds.reduce((maxWidth, cfId) => {
      const field = customFieldsById[cfId];
      const sourceValue = isCreateMode ? values[cfId] : formValues[cfId];
      const text = field
        ? getCustomFieldDisplayValue(field, sourceValue)
        : "-";
      const estimatedWidth = Math.min(
        text.length * MOBILE_CHAR_WIDTH_PX + 96,
        MOBILE_VALUE_COLUMN_MAX_WIDTH,
      );
      return Math.max(maxWidth, estimatedWidth);
    }, MOBILE_VALUE_COLUMN_MIN_WIDTH);
  }, [
    customFieldsById,
    displayedFieldIds,
    formValues,
    isCreateMode,
    isMobile,
    values,
  ]);

  const handleAddFields = useCallback(() => {
    for (const cfId of selectedFieldIds) {
      const defaultValue = getDefaultValueByType(customFieldsById[cfId]?.type);
      onUpdateValue?.({ customFieldId: cfId, value: defaultValue });
    }
    setAddFieldDialogOpen(false);
    setSelectedFieldIds([]);
  }, [selectedFieldIds, customFieldsById, onUpdateValue]);

  const toggleFieldSelection = useCallback((cfId: string) => {
    setSelectedFieldIds((prev) =>
      prev.includes(cfId) ? prev.filter((id) => id !== cfId) : [...prev, cfId],
    );
  }, []);

  const allAvailableFields = useAvailableCustomFields(fieldIds);

  // The command list does no filtering of its own, so the match set —
  // name, key, type and type label — is computed here.
  const availableFields = useMemo(() => {
    const q = addFieldQuery.trim().toLowerCase();
    if (!q) return allAvailableFields;
    return allAvailableFields.filter((field) =>
      [field.name, field.key ?? "", field.type, getFieldTypeMeta(field.type).label]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [allAvailableFields, addFieldQuery]);

  const addFieldItems = (
    <>
      <CommandGroup>
        {/* Plain result count, not a palette section label — opt out of the
            command label's default uppercase/letter-spaced styling. The old
            `heading`/`headingClassName` props are a `CommandGroupLabel` part. */}
        <CommandGroupLabel className="normal-case tracking-normal">
          {`${availableFields.length} Result${availableFields.length !== 1 ? "s" : ""}`}
        </CommandGroupLabel>
        {availableFields.map((field) => {
          const isSelected = selectedFieldIds.includes(field.customFieldId);
          const meta = getFieldTypeMeta(field.type);
          const desc = [
            field.key,
            field.createdBy?.name ? `Created by ${field.createdBy.name}` : null,
            !isMobile ? formatRelativeDate(field.creationDate) : null,
          ]
            .filter(Boolean)
            .join("  ·  ");
          return (
            // Base UI's autocomplete item has no `keywords` prop and no
            // `onSelect` — matching is the local `matchesFieldQuery` filter
            // above, and selection is an ordinary click.
            <CommandItem
              key={field.customFieldId}
              value={field.customFieldId}
              onClick={() => toggleFieldSelection(field.customFieldId)}
            >
              {/* Checkbox has no label slot, so a `Label` wraps both parts.
                  The row itself owns the press, so the box is visual only. */}
              <Label className="w-full min-w-0">
                <Checkbox
                  checked={isSelected}
                  disabled
                  className="pointer-events-none opacity-100"
                />
                <Box direction="row" align="center" gap="sm" minWidth={0}>
                  <Icon name={meta.icon} size="md" color={meta.color} />
                  <Box fill="flex" gap="xs" overflow="hidden" minWidth={0}>
                    <Text size="sm" weight="medium" truncate={1}>
                      {field.name}
                    </Text>
                    {desc ? (
                      <Text size="xs" variant="muted" truncate={1}>
                        {desc}
                      </Text>
                    ) : null}
                  </Box>
                </Box>
              </Label>
            </CommandItem>
          );
        })}
      </CommandGroup>
      <CommandEmpty>No fields found.</CommandEmpty>
    </>
  );

  const headerActions = (
    <Box direction="row" align="center" gap="xs">
      {hasFields && !searchVisible && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Search custom fields"
          onClick={() => setSearchVisible(true)}
        >
          <Icon name="search" size="md" />
        </Button>
      )}
      {searchVisible && (
        <Box direction="row" align="center" gap="xs">
          <Box direction="row" align="center" width={180}>
            <Input
              value={searchValue}
              onChange={(event) => setInternalSearchValue(event.target.value)}
              placeholder="Search..."
              size="sm"
              autoFocus
            />
          </Box>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close search"
            onClick={handleCloseSearch}
          >
            <Icon name="x" size="md" />
          </Button>
        </Box>
      )}
      {!isCreateMode && !readOnly && (
        <Menu>
          <MenuTrigger
            render={
              <Button variant="ghost" size="icon-sm" aria-label="Add custom field">
                <Icon name="plus" size="md" />
              </Button>
            }
          />
          <MenuPopup align="end">
            {canManageCustomFields && (
              <MenuItem onClick={handleNavigateToCreateField}>
                <Icon name="plus" size="md" />
                <Text>Create field...</Text>
              </MenuItem>
            )}
            <MenuItem onClick={handleOpenAddFieldDialog}>
              <Icon name="book-open" size="md" />
              <Text>Add field from workspace...</Text>
            </MenuItem>
          </MenuPopup>
        </Menu>
      )}
    </Box>
  );

  return (
    <Box rounded="sm" gap="sm" border paddingTop={hideHeader ? "sm" : undefined}>
      {hideHeader ? null : (
        <Box
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          paddingX="md"
          paddingTop="sm"
        >
          <Text size="lg" weight="semibold">
            Custom fields
          </Text>
          {headerActions}
        </Box>
      )}

      {/* Fields list or empty state */}
      {hasFields ? (
        <Box direction="column" paddingX="md">
          {(() => {
            const tableContent = (
              <>
                <InlineHeaderRow suppressDivider={headerDividerSuppressed}>
                    <Box
                      paddingX="md"
                      paddingY="sm"
                      justify="center"
                      width={
                        mobileFieldColumnWidth ?? DESKTOP_FIELD_COLUMN_WIDTH
                      }
                      shrink={false}
                    >
                      <Text size="xs" weight="semibold" variant="muted">
                        Field
                      </Text>
                    </Box>
                    <Box
                      fill={mobileValueColumnWidth ? undefined : "flex"}
                      width={mobileValueColumnWidth}
                      shrink={!mobileValueColumnWidth}
                      paddingX="md"
                      paddingY="sm"
                      justify="center"
                    >
                      <Text size="xs" weight="semibold" variant="muted">
                        Value
                      </Text>
                    </Box>
                  </InlineHeaderRow>
                {displayedFieldIds.map((cfId, index) => {
                  const field = customFieldsById[cfId];
                  const nextFieldId = displayedFieldIds[index + 1] ?? null;
                  return (
                    <CustomFieldRow
                      key={cfId}
                      id={`cf-${cfId}`}
                      cfId={cfId}
                      field={field}
                      value={isCreateMode ? values[cfId] : formValues[cfId]}
                      isActive={activeFieldId === cfId}
                      fieldColumnWidth={mobileFieldColumnWidth}
                      valueColumnWidth={mobileValueColumnWidth}
                      required={requiredSet.has(cfId)}
                      error={fieldErrors[cfId]}
                      showRemove={!isCreateMode && !readOnly}
                      highlighted={highlightedFieldId === cfId}
                      beforeHighlighted={highlightedFieldId === nextFieldId}
                      onRowHighlightChange={handleRowHighlightChange}
                      onInvalidStateChange={handleInvalidStateChange}
                      onUpdate={handleFieldUpdate}
                      onAddPhoto={onAddPhoto}
                      onRemovePhoto={onRemovePhoto}
                      onRemove={handleRemoveField}
                      onActivate={handleActivateField}
                      onDeactivate={handleDeactivateField}
                      readOnly={readOnly}
                      downloadPrefix={issue?.name}
                    />
                  );
                })}
              </>
            );

            if (!isMobile) return tableContent;

            const minTableWidth =
              (mobileFieldColumnWidth ?? DESKTOP_FIELD_COLUMN_WIDTH) +
              (mobileValueColumnWidth ?? MOBILE_VALUE_COLUMN_MIN_WIDTH);

            return (
              // The scrollbar IS user-visible chrome here (a horizontally
              // scrolled table on mobile), so this is a ScrollArea, not a plain
              // overflow Box. ScrollArea renders both scrollbars and reveals
              // whichever axis overflows.
              // `clampContentMinWidth={false}` is required: the default pins
              // the content to `min-width: 0`, which would cancel the table's
              // intrinsic width and kill the horizontal scroll.
              <ScrollArea className="w-full" clampContentMinWidth={false}>
                <Box minWidth={minTableWidth}>{tableContent}</Box>
              </ScrollArea>
            );
          })()}
        </Box>
      ) : isCreateMode ? (
        <Box paddingX="md">
          <Text size="sm" variant="muted">
            No custom fields for this issue type
          </Text>
        </Box>
      ) : (
        <Box paddingX="md" paddingBottom="md" paddingTop="xs" align="start">
          <Menu>
            <MenuTrigger
              render={
                <Button variant="ghost" size="lg" className="w-full">
                  <Icon name="plus" size="lg" color="muted" />
                  <Text size="sm" variant="muted">
                    Create or add custom field to this location
                  </Text>
                </Button>
              }
            />
            <MenuPopup align="start">
              {canManageCustomFields && (
                <MenuItem onClick={handleNavigateToCreateField}>
                  <Icon name="plus" size="md" />
                  <Text>Create field...</Text>
                </MenuItem>
              )}
              <MenuItem onClick={handleOpenAddFieldDialog}>
                <Icon name="book-open" size="md" />
                <Text>Add field from workspace...</Text>
              </MenuItem>
            </MenuPopup>
          </Menu>
        </Box>
      )}

      {/* Show/hide fields toggle */}
      {searchValue ? null : isCreateMode &&
        requiredSet.size > 0 &&
        fieldIds.length > requiredSet.size ? (
        <Box direction="row" paddingX="md" paddingBottom="md">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowOnlyRequired((prev) => !prev)}
          >
            <Icon
              name={showOnlyRequired ? "chevron-down" : "chevron-up"}
              size="md"
              color="muted"
            />
            <Text size="sm" variant="muted">
              {showOnlyRequired ? "Show all fields" : "Show required fields"}
            </Text>
          </Button>
        </Box>
      ) : !isCreateMode && numberOfEmptyFields > 0 ? (
        <Box direction="row" paddingX="md" paddingBottom="md">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowEmptyFields((prev) => !prev)}
          >
            <Icon
              name={showEmptyFields ? "chevron-down" : "chevron-up"}
              size="md"
              color="muted"
            />
            <Text size="sm" variant="muted">
              {showEmptyFields ? "Show" : "Hide"} {numberOfEmptyFields} empty
              field{numberOfEmptyFields !== 1 ? "s" : ""}
            </Text>
          </Button>
        </Box>
      ) : null}

      {/* Add field from workspace — mobile drawer / desktop command dialog.
          The command list does not filter itself; the explicit pass above
          does. */}
      {isMobile ? (
        <Drawer
          open={addFieldDialogOpen}
          onOpenChange={(open: boolean) => {
            setAddFieldDialogOpen(open);
            if (!open) setSelectedFieldIds([]);
          }}
          // `onOpenChangeComplete` is the settle signal: it gates the search
          // autofocus so the keyboard never opens mid-animation.
          onOpenChangeComplete={handleSheetSettle}
          snapPoints={ADD_FIELD_SNAP_POINTS}
        >
          <DrawerPopup showBar>
            <DrawerHeader>
              <Box direction="row" align="center" justify="between" gap="sm">
                <DrawerTitle>Add custom fields</DrawerTitle>
                <Box direction="row" align="center" gap="xs" shrink={false}>
                  {canManageCustomFields && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                      aria-label="Manage custom fields"
                      onClick={handleNavigateToCreateField}
                    >
                      <Icon name="settings" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    className="rounded-full"
                    aria-label="Add selected fields"
                    onClick={handleAddFields}
                    disabled={selectedFieldIds.length === 0}
                  >
                    <Icon name="check" />
                  </Button>
                </Box>
              </Box>
            </DrawerHeader>
            <Command
              mode="none"
              value={addFieldQuery}
              onValueChange={setAddFieldQuery}
            >
              <CommandInput
                placeholder="Search custom fields..."
                autoFocus={canAutoFocus}
              />
              <DrawerPanel className="px-0">
                <CommandList>{addFieldItems}</CommandList>
              </DrawerPanel>
            </Command>
          </DrawerPopup>
        </Drawer>
      ) : (
        <CommandDialog
          open={addFieldDialogOpen}
          onOpenChange={(open: boolean) => {
            setAddFieldDialogOpen(open);
            if (!open) setSelectedFieldIds([]);
          }}
        >
          <CommandDialogPopup
            aria-label="Add custom fields"
          >
            <Command
              mode="none"
              value={addFieldQuery}
              onValueChange={setAddFieldQuery}
            >
              <CommandInput placeholder="Search custom fields..." autoFocus />
              <Box direction="column" grow minHeight={0} maxHeight={510}>
                <CommandList>{addFieldItems}</CommandList>
              </Box>
            </Command>

            {/* Footer */}
            <Separator />
            <Box direction="row" align="center" justify="between" padding="md">
              {canManageCustomFields ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNavigateToCreateField}
                >
                  <Text size="sm" variant="muted">
                    Manage fields
                  </Text>
                </Button>
              ) : (
                // Placeholder keeps the cancel/add group right-aligned
                // under justify="between" when the manage button is hidden.
                <Box />
              )}
              <Box direction="row" align="center" gap="sm">
                {selectedFieldIds.length > 0 && (
                  <Text size="sm" variant="muted">
                    {selectedFieldIds.length} selected
                  </Text>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddFieldDialogOpen(false)}
                >
                  <Text>Cancel</Text>
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddFields}
                  disabled={selectedFieldIds.length === 0}
                >
                  <Text>Add fields</Text>
                </Button>
              </Box>
            </Box>
          </CommandDialogPopup>
        </CommandDialog>
      )}
    </Box>
  );
}

// Snap points are fractions of the viewport height, the Drawer's scale.
const ADD_FIELD_SNAP_POINTS = [0.92];
